'use client';

import { useMemo, useRef, useState } from 'react';
import { UserSettings } from '../../../environmentVariables/EnvironmentVariables';
import {
	BOOSTED_MULITPLIER,
	TURBO_MULTIPLIER,
} from '../../../providers/priorityFees/consts';
import usePriorityFeeStore from '../../../stores/usePriorityFeeStore';
import {
	PriorityFeeCalculator,
	SANITY_CHECK_ABS_MAX_FEE_IN_SOL,
} from '../../../utils/PriorityFeeCalculator';
import Input from '../../primitives/Input/Input';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { MarketId } from '@velocity-exchange/common';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { LAMPORTS_EXP } from '@velocity-exchange/sdk';
import { Settings } from 'src/components/Settings/SettingsForm';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';

const lamportsPerSol = LAMPORTS_PER_SOL;
const lamportsPrecisionExp = LAMPORTS_EXP.toNumber();
const MIN_SOL_STR = (1 / lamportsPerSol).toFixed(lamportsPrecisionExp);

const PriorityFeeSettingsInputs = ({
	localSettings,
	setLocalSettings,
}: {
	localSettings: Settings;
	setLocalSettings: (setting: Settings) => void;
}) => {
	const initialSettingsRef = useRef(localSettings);
	const currentSettingsRef = useRef(localSettings);

	const getPriorityFeeToUse = usePriorityFeeStore((s) => s.getPriorityFeeToUse);

	const getOraclePrice = useGetOraclePriceForMarket();

	const localPriorityFeeSetting = useRef(localSettings.priorityFee);

	const localCustomPriorityFee = useRef<string>(
		localSettings.customPriorityFee
			?.toFixed(lamportsPrecisionExp) // Use toFixed to prevent small numbers being formatted as scientific notation
			.replace(/\.?0+$/, '') // Trim any trailing zeros
	);

	const localPriorityFeeMaxCap = useRef(
		localSettings?.maxPriorityFeeCap?.toString()
	);

	const [showMaxFeeMessage, setShowMaxFeeMessage] = useState(false);

	const solMarketId = MarketId.createPerpMarket(0);

	const updateSettingsValue = (updatedSettings: Partial<UserSettings>) => {
		const newSettings = {
			...currentSettingsRef.current,
			...updatedSettings,
		};

		setLocalSettings(newSettings);

		currentSettingsRef.current = newSettings;
	};

	const getPriorityFeeEstimateInSol = (
		setting: UserSettings['priorityFee']
	) => {
		return PriorityFeeCalculator.getPriorityFeeInSolForComputeUnitPrice(
			getPriorityFeeToUse(setting)
		);
	};

	const getPriorityFeeEstimateInUsd = (setting: UserSettings['priorityFee']) =>
		convertSolToUsdString(getPriorityFeeEstimateInSol(setting));

	const convertSolToUsdString = (solAmount: number) => {
		const usdAmount = solAmount * (getOraclePrice(solMarketId)?.toNum() ?? 0);

		if (isNaN(usdAmount)) {
			return '';
		} else if (usdAmount < 0.01) {
			return '<$0.01';
		} else {
			return `~$${usdAmount.toFixed(2)}`;
		}
	};

	const priorityFeeButtonOptions: {
		value: UserSettings['priorityFee'];
		label: string;
		subLabel?: string;
	}[] = useMemo(
		() => [
			{
				value: 'boosted',
				label: 'Boosted',
				subLabel: `${BOOSTED_MULITPLIER}x`,
			},
			{ value: 'turbo', label: 'Turbo', subLabel: `${TURBO_MULTIPLIER}x` },
			{ value: 'custom', label: 'Custom' },
		],
		[]
	);

	const handleFeeOptionChange = (value: UserSettings['priorityFee']) => {
		if (value === 'custom') {
			updateSettingsValue({
				priorityFee: 'custom',
				...(initialSettingsRef.current.customPriorityFee !== undefined && {
					customPriorityFee: initialSettingsRef.current.customPriorityFee,
				}),
			});
			localCustomPriorityFee.current =
				initialSettingsRef.current.customPriorityFee?.toString() ?? '';
		} else {
			updateSettingsValue({ priorityFee: value, customPriorityFee: undefined });
			localCustomPriorityFee.current = '';
		}
		localPriorityFeeSetting.current = value;
		setShowMaxFeeMessage(false);
	};

	const showMaxLowerThanEstMessage = useMemo(
		() =>
			localPriorityFeeSetting.current !== 'custom' &&
			parseFloat(localPriorityFeeMaxCap.current) <
				getPriorityFeeEstimateInSol(localPriorityFeeSetting.current),
		[localPriorityFeeMaxCap.current, localPriorityFeeSetting.current]
	);

	const handleCustomPriorityFeeChange = (value: string) => {
		try {
			let valueNum = +value;
			if (!isNaN(valueNum) && valueNum >= 0) {
				if (valueNum > SANITY_CHECK_ABS_MAX_FEE_IN_SOL) {
					setShowMaxFeeMessage(true);

					updateSettingsValue({
						priorityFee: 'custom',
						customPriorityFee: SANITY_CHECK_ABS_MAX_FEE_IN_SOL,
					});

					localPriorityFeeSetting.current = 'custom';
					localCustomPriorityFee.current = `${SANITY_CHECK_ABS_MAX_FEE_IN_SOL}`;
				} else {
					// manually enforce 1 lamport as minimum size since 1e-9 has issues with generic component
					const decimalPlaces = value?.split('.')?.[1]?.length ?? 0;
					if (decimalPlaces > lamportsPrecisionExp) {
						value = MIN_SOL_STR;
						valueNum = +value;
					}
					updateSettingsValue({
						priorityFee: 'custom',
						customPriorityFee: valueNum,
					});

					localPriorityFeeSetting.current = 'custom';
					localCustomPriorityFee.current = value;
					setShowMaxFeeMessage(false);
				}
			} else {
				localCustomPriorityFee.current = '';
			}
		} catch (e) {
			return;
		}
	};

	const handleMaxPriorityFeeCapChange = (value: string) => {
		let valueNum = +value;

		if (!isNaN(valueNum) && valueNum >= 0) {
			if (valueNum > SANITY_CHECK_ABS_MAX_FEE_IN_SOL) {
				valueNum = SANITY_CHECK_ABS_MAX_FEE_IN_SOL;
				value = '1';
				setShowMaxFeeMessage(true);
			} else {
				setShowMaxFeeMessage(false);
			}

			// manually enforce 1 lamport as minimum size since 1e-9 has issues with generic component
			const decimalPlaces = value?.split('.')?.[1]?.length ?? 0;
			if (decimalPlaces > 9) {
				value = MIN_SOL_STR;
				valueNum = +value;
			}

			updateSettingsValue({
				maxPriorityFeeCap: valueNum,
				//priorityFee: localPriorityFeeSetting.current,
			});
			localPriorityFeeMaxCap.current = value;
		} else {
			localPriorityFeeMaxCap.current = '';
		}
	};

	return (
		<div className="flex flex-col gap-y-md">
			<div className="flex flex-col gap-y-sm">
				<h3 className="typo-label-sm text-text-default">Priority Fee</h3>
				<p className="typo-body-xs text-text-secondary">
					An extra fee added to your transactions to encourage Solana validators
					to process your transaction faster. Set a cap to prevent overpaying.
				</p>
			</div>

			<RadioGroup.Root
				value={localPriorityFeeSetting.current}
				onValueChange={handleFeeOptionChange}
				className="grid grid-cols-2 gap-md sm:grid-cols-4"
			>
				<RadioGroup.Item
					id="priority-fee-dynamic"
					variant="card"
					layout="stacked"
					size="md"
					value="dynamic"
					label="Dynamic"
					subLabel={
						<div className="flex items-center gap-x-1">
							<MarketIcon sizeClass="size-3" marketSymbol="SOL" />
							<span>{getPriorityFeeEstimateInSol('dynamic')?.toFixed(5)}</span>
						</div>
					}
				/>
				{priorityFeeButtonOptions.map((option) => (
					<RadioGroup.Item
						key={option.value}
						id={`priority-fee-${option.value}`}
						variant="card"
						layout="stacked"
						size="md"
						value={option.value}
						label={option.label}
						subLabel={option.subLabel}
					/>
				))}
			</RadioGroup.Root>

			{localPriorityFeeSetting.current === 'dynamic' && (
				<p className="typo-body-xs text-text-secondary">
					Automatically adjusts, targeting the 75th percentile of fees in the
					last 10 blocks.
				</p>
			)}

			{localPriorityFeeSetting.current === 'custom' ? (
				<Input
					id="custom-priority-fee"
					type="number"
					labelVariant="standard"
					label="Custom Fee"
					value={localCustomPriorityFee.current?.toString()}
					onChange={handleCustomPriorityFeeChange}
					suffix="SOL"
				/>
			) : (
				<div className="flex w-full items-center justify-between gap-2">
					<Input
						id="est-fee"
						type="number"
						labelVariant="standard"
						label={
							<div className="flex items-center justify-between">
								<span>Est. Fee</span>
								<span>
									{getPriorityFeeEstimateInUsd(localPriorityFeeSetting.current)}
								</span>
							</div>
						}
						labelClassName="flex justify-between [&>label]:w-full"
						value={`${parseFloat(
							getPriorityFeeEstimateInSol(
								localPriorityFeeSetting.current
							)?.toFixed(9)
						)}`}
						onChange={() => {}}
						suffix="SOL"
						disabled
					/>

					<Input
						id="max-fee-cap"
						labelVariant="standard"
						type="number"
						label={
							<div className="flex items-center justify-between">
								<span>Max Fee Cap</span>
								<span>
									{convertSolToUsdString(
										parseFloat(localPriorityFeeMaxCap.current)
									)}
								</span>
							</div>
						}
						labelClassName="flex justify-between [&>label]:w-full"
						value={localPriorityFeeMaxCap.current}
						onChange={handleMaxPriorityFeeCapChange}
						suffix="SOL"
					/>
				</div>
			)}

			{showMaxFeeMessage && (
				<InlineAlert
					variant="warning"
					title={`Max fee is ${SANITY_CHECK_ABS_MAX_FEE_IN_SOL} SOL`}
				/>
			)}

			{showMaxLowerThanEstMessage && (
				<InlineAlert
					variant="warning"
					title="Max fee is less than current estimate. Transactions may time out."
				/>
			)}
		</div>
	);
};

export default PriorityFeeSettingsInputs;
